import { styled } from '@material-ui/styles';
import React from 'react';

import { GestureEvent } from '../../../../../Events/withEvent';
import { useIsMobile } from '../../../../../Web/Context/PlatformContext/PlatformContext';
import {
    InitialGestureContextState as GestureContextInitialState,
    useGestureContext,
    useMultiTouch,
} from '../../../Context/GestureContext';
import { useMapContextState, useMapProvider } from '../../../Context/MapContext';
import { useMapViewerContext } from '../../../Context/MapViewerContext';
import { useUndoRedoMap } from '../../../Context/UndoRedoMapContext';
import { MapConstants, MapMode } from '../../../MapConstants';
import { IRoom, RoomType } from '../../../Models/IRoom';
import { EventUtils } from '../../../Utils/EventUtils';
import { RoomMove, useRoomMove } from '../../../Utils/Room/RoomMove';
import { CoordPoint } from '../../../Utils/Types';
import { SvgAttributesWith } from 'Plugins/FloorPlan/Widgets/SvgTypes';

type BoxWallsProps = {
    readonly?: boolean;
    roomId?: string;
    rooms?: Array<IRoom>;
    selectedRoomId?: string;
    roomType?: RoomType;
};
export const MapBoxWalls = (props: SvgAttributesWith<unknown>) => {
    const { type, rooms = [], selectedRoomId } = useMapContextState();
    return <RawBoxWalls rooms={rooms} selectedRoomId={selectedRoomId} roomType={type} {...props} />;
};

//* todo : to review
// const { type: typeFromMap } = useMapContextState();
// la method :arrow_up: ne devrait pas exister ici
export const RawBoxWalls = (props: SvgAttributesWith<BoxWallsProps>) => {
    const { readonly, roomId, rooms = [], selectedRoomId, roomType, ...attrs } = props;
    const { type: typeFromMap } = useMapContextState();
    return (
        <g id="RawBoxWalls" {...attrs}>
            {rooms
                .filter((item) => (roomId ? item.roomId === roomId : true))
                .filter((room) => readonly || room.type === (roomType || typeFromMap))
                .map((room) => (
                    <BoxWall
                        key={`RawBoxWall-${room.roomId}`}
                        room={room}
                        selected={room.roomId === selectedRoomId}
                        readonly={readonly}
                    />
                ))}
        </g>
    );
};

type BoxWallProps = { room: IRoom; selected?: boolean; readonly?: boolean };

const formatPoint = (coord: CoordPoint) => `${coord.x},${coord.y}`;

const BoxWall = ({ room, selected, readonly }: BoxWallProps) => {
    const { type } = useMapContextState();
    const GESTURE = useBoxWallsGesture(room);
    const gestureContext = useGestureContext();
    const allPointsPoints = room.walls?.map((w) => formatPoint(w.start)).join(' ');
    const wall = room.walls[0];
    const points = allPointsPoints + ' ' + formatPoint(wall.start);
    const isValid = (gestureContext.state.invalidRoomIds ?? []).indexOf(room.roomId!) === -1;
    const invalidColor = selected ? '#741313' : '#74131325';
    let validColor = '#FFFFFF';
    let stroke = '#1c1d24';
    if (!readonly) {
        if (type === RoomType.Sol) {
            validColor = selected ? '#13123A80' : '#131A3A26';
            stroke = selected ? '#131A3A' : '#131A3A';
        } else {
            validColor = selected ? '#FF7199' : '#FF719926';
            stroke = '#FF7199';
        }
    }

    const props: React.SVGProps<SVGPolygonElement> = {
        id: RoomMove.roomWallsId(wall.roomId!),
        fill: readonly ? validColor : isValid ? validColor : invalidColor,
        stroke,
        strokeWidth: MapConstants.wallThickness,
        points,
        //@ts-ignore
        'start-point': formatPoint(wall.start),
        pointerEvents: 'auto',
    };

    return (
        <g className={RoomMove.class(wall.roomId!)} id={RoomMove.roomWallsGroupId(wall.roomId!)}>
            <WallSvg
                {...props}
                onMouseDown={GESTURE.onPressDown}
                onTouchStart={GESTURE.onPressDown}
                onMouseUp={GESTURE.onMouseUp}
                onTouchEnd={GESTURE.onTouchEnd}
            />
            {room.coords?.map((coord, index) => (
                <circle
                    key={`Room-${room.roomId}-Point-${index}`}
                    cx={coord.x}
                    cy={coord.y}
                    r={2}
                    strokeWidth={2}
                    stroke="black"
                    fill="white"
                />
            ))}
        </g>
    );
};

export type SvgGraphProps = React.SVGProps<SVGPolygonElement | SVGPolylineElement>;
const WallSvg = (props: SvgGraphProps) => {
    return <WallPolygon {...props} style={{ cursor: 'pointer' }} />;
};

//* HOVER COLOR fill: '#18335e'
const WallPolygon = styled('polygon')({ '&:hover': {} });

const useBoxWallsGesture = (room: IRoom) => {
    const isMobile = useIsMobile();
    const map = useMapProvider();
    const undoMap = useUndoRedoMap();
    const roomGesture = useRoomMove();
    const mapViewer = useMapViewerContext();
    const gestureContext = useGestureContext();

    const multiTouch = useMultiTouch();

    const onPressDown = (pressDownEvent: GestureEvent) => {
        if (!multiTouch.handleMultiTouch(pressDownEvent as React.TouchEvent)) {
            const { roomMoveInit } = map.getState();

            if (MapConstants.mode === undefined && !roomMoveInit) {
                gestureContext.update({ isPressDown: true });
                const snap = EventUtils.calculSnapFromSyntheticEvent(
                    pressDownEvent,
                    MapConstants.grid_snap,
                    mapViewer.svgViewer!.getValue(),
                    (mapViewer.svgViewer as any)?.ViewerDOM
                );
                const mapState = map.getState();
                undoMap.store(mapState);
                map.update({
                    values: {
                        roomMove: room.roomId,
                        roomMoveInit: roomGesture.pressDownRoom(
                            snap,
                            mapState.rooms?.find((r) => r.roomId === room.roomId)!
                        ),
                        panZoomDisabled: true,
                    },
                    logs: { event: 'useBoxWallsGesture onPressDown' },
                });
                MapConstants.mode = MapMode.MODE_TYPE_MOVE_ROOM;
            }
        }
    };

    const onMouseUp = () => {
        const { isPressDown, menuContextOpenRoomId } = gestureContext.getState();
        if (
            !isMobile &&
            isPressDown &&
            menuContextOpenRoomId !== room.roomId &&
            MapConstants.mode === MapMode.MODE_TYPE_MOVE_ROOM
        ) {
            gestureContext.update({ ...GestureContextInitialState, menuContextOpenRoomId: room.roomId });
            if (map.getState().selectedRoomId !== room.roomId && !isMobile) {
                map.setPanel({ values: { selectedRoomId: room.roomId, panZoomDisabled: false, busy: false } });
            }
            MapConstants.mode = undefined;
        }
    };

    const onTouchEnd = React.useCallback(
        (event: GestureEvent) => {
            const isPressDown = gestureContext.state.isPressDown;
            if (
                isPressDown &&
                MapConstants.mode !== MapMode.MODE_TYPE_OPENING_PLACING &&
                MapConstants.mode !== MapMode.MODE_TYPE_ROOM_ITEM_PLACING
            ) {
                if (gestureContext.state.menuContextOpenRoomId !== room.roomId) {
                    gestureContext.update({ ...GestureContextInitialState, menuContextOpenRoomId: room.roomId });
                } else {
                    gestureContext.clear();
                }
                map.setPanel({ values: { panZoomDisabled: false, busy: false } });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gestureContext.state.isPressDown, gestureContext.state.menuContextOpenRoomId]
    );

    return { onPressDown, onMouseUp, onTouchEnd };
};
